import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

type Variant = "default" | "stat" | "compact";

interface CardGridSkeletonProps {
  count?: number;
  cols?: 1 | 2 | 3 | 4;
  variant?: Variant;
  className?: string;
}

const colsClass: Record<NonNullable<CardGridSkeletonProps["cols"]>, string> = {
  1: "grid-cols-1",
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
  4: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
};

export function CardGridSkeleton({
  count = 6,
  cols = 3,
  variant = "default",
  className,
}: CardGridSkeletonProps) {
  return (
    <div className={cn("grid gap-4", colsClass[cols], className)}>
      {Array.from({ length: count }).map((_, i) => (
        <CardSkeleton key={i} variant={variant} />
      ))}
    </div>
  );
}

function CardSkeleton({ variant }: { variant: Variant }) {
  if (variant === "stat") {
    return (
      <div className="rounded-lg border border-border/50 bg-card p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-5 rounded" />
          <Skeleton className="h-3 w-20" />
        </div>
        <Skeleton className="h-7 w-24" />
        <Skeleton className="h-3 w-16" />
      </div>
    );
  }

  if (variant === "compact") {
    return (
      <div className="rounded-lg border border-border/50 bg-card p-3 space-y-2">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border/50 bg-card p-4 space-y-3">
      <div className="flex items-center gap-3">
        <Skeleton className="h-10 w-10 rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      </div>
      <Skeleton className="h-3 w-full" />
      <Skeleton className="h-3 w-5/6" />
      <div className="flex gap-2 pt-1">
        <Skeleton className="h-8 w-20 rounded-md" />
        <Skeleton className="h-8 w-20 rounded-md" />
      </div>
    </div>
  );
}
